import { useMemo, useState, useEffect, useRef, useCallback } from "react";
import type { OpsApi } from "../useOps";
import { SEVERITY_COLOR, type Severity, type Signal } from "@/types/api";
import { ruleTag, timeAgo, signalAge, utcClock } from "@/lib/normalize";
import { SituationTree } from "./SituationTree";
import { config } from "@/config";
import { getIdToken } from "@/lib/auth-context";

/**
 * DetailRail — right column. Shows a Situation Overview when nothing is
 * selected, a Signal drill-down when a signal is active, or an Event drill-down
 * when an event marker is active.
 */
export function DetailRail({ ops }: { ops: OpsApi }) {
  const client = ops.getClient();
  const signal = ops.selectedSignalId
    ? client?.signals.find((s) => s.id === ops.selectedSignalId)
    : null;
  const event = ops.selectedEventId
    ? client?.events.find((e) => e.id === ops.selectedEventId)
    : null;

  const label = signal ? "Signal Detail" : event ? "Event Detail" : "Situation Overview";

  return (
    <aside id="detail">
      <div className="detail-head">
        <span className="ph-label">{label}</span>
        {(signal || event) && (
          <span className="mono-xs" style={{ cursor: "pointer" }} onClick={ops.clearSelection} title="Close detail view and reset map">
            ✕ CLOSE
          </span>
        )}
      </div>
      <div className="detail-body">
        {signal ? (
          <SignalDetailBody ops={ops} signalId={signal.id} />
        ) : event ? (
          <EventDetailBody ops={ops} eventId={event.id} />
        ) : (
          <OverviewBody ops={ops} />
        )}
      </div>
      <div className="rail-handle right" id="detailHandle" />
    </aside>
  );
}

export function SignalDetailBody({ ops, signalId }: { ops: OpsApi; signalId: string }) {
  const s = ops.getClient()?.signals.find((x) => x.id === signalId);
  const events = ops.getClient()?.events ?? [];
  if (!s) return <div className="empty-hint">Signal no longer active.</div>;
  // Evidence + sources ride in the raw wire payload (the backend sends the full insight) — surface
  // them so the paying user sees the same proof the internal dashboard does.
  const raw = s.raw as unknown as { evidence?: Array<{ label: string; value: string; url?: string | null }>; sources?: string[] };
  const evidence = Array.isArray(raw?.evidence) ? raw.evidence : [];
  return (
    <>
      <div className="dsec">
        <div className="d-title">{s.title}</div>
        <div className="d-meta">
          <span
            className="sevtag"
            style={{
              ["--sev" as string]: SEVERITY_COLOR[s.severity],
              color: SEVERITY_COLOR[s.severity],
              borderColor: SEVERITY_COLOR[s.severity],
            }}
          >
            {s.severity}
          </span>
          {s.status === 'live' && <span className="lifecycle-badge live">LIVE</span>}
          {s.status === 'resolved' && <span className="lifecycle-badge resolved">RESOLVED{s.resolvedAt ? ` ${new Date(s.resolvedAt).toLocaleTimeString()}` : ''}</span>}
          {s.status === 'live' && !s.statusReliable && <span className="lifecycle-badge unknown">STATUS UNKNOWN</span>}
          {s.isRoot && <span className="lifecycle-badge root">ROOT</span>}
          {s.parentId && <span className="lifecycle-badge child">CHILD</span>}
          <span className="d-id">{ruleTag(s.rule) || "SIGNAL"}</span>
        </div>
        <div className="d-meta">
          <span className="d-id">
            {s.region} · {s.lat.toFixed(2)}, {s.lon.toFixed(2)} · {signalAge(s.createdAt, s.updatedAt)}
          </span>
        </div>
      </div>

      {s.summary && (
        <div className="dsec">
          <div className="h">Summary</div>
          <div className="sum">{s.summary}</div>
        </div>
      )}

      {(s.confidenceRate != null || s.estimatedEffect) && (
        <div className="dsec">
          <div className="h">Signal Score</div>
          <div className="score-pair">
            {s.confidenceRate != null && (
              <div className="score-box">
                <div className="score-val">{Math.round(s.confidenceRate * 100)}%</div>
                <div className="score-label">Confidence</div>
              </div>
            )}
            {s.estimatedEffect && (
              <div className="score-box">
                <div className="score-val">{s.estimatedEffect.sigma.toFixed(2)}σ</div>
                <div className="score-label">Est. effect · {s.estimatedEffect.pct.toFixed(2)}%</div>
              </div>
            )}
          </div>
        </div>
      )}

      <SignalTrackRecord rule={s.rule} severity={s.severity} />

      {s.recommendedAction && (
        <div className="dsec">
          <div className="h">Recommended Action</div>
          <div className="action-box">
            <div className="h">Desk Note</div>
            <div className="t">{s.recommendedAction}</div>
          </div>
        </div>
      )}

      {s.tickers.length > 0 && (
        <div className="dsec">
          <div className="h">Tickers</div>
          <div className="tickers">
            {s.tickers.map((t) => (
              <span className="tk" key={t}>
                {t}
              </span>
            ))}
          </div>
        </div>
      )}

      {s.tickers.length > 0 && (
        <div className="dsec">
          <div className="h">Price</div>
          <PriceChart signalId={s.id} ticker={s.tickers[0]} eventMs={s.createdAt} />
        </div>
      )}

      <div className="dsec">
        <div className="h">Affected Assets</div>
        <div className="assets">
          {s.assets.length ? (
            s.assets.map((a, i) => (
              <div className="asset" key={i}>
                <div>
                  <div className="nm">{a.name || "—"}</div>
                  <div className="kd">
                    {a.kind}
                    {a.country ? ` · ${a.country}` : ""}
                  </div>
                </div>
                <div className="ds">
                  {a.distKm == null ? "—" : a.distKm === 0 ? "on-site" : `${a.distKm.toFixed(1)} km`}
                </div>
              </div>
            ))
          ) : (
            <div className="empty-hint">No fixed assets within range.</div>
          )}
        </div>
      </div>

      {evidence.length > 0 && (
        <div className="dsec">
          <div className="h">Evidence</div>
          <div className="assets">
            {evidence.slice(0, 8).map((e, i) => (
              <div className="asset" key={i}>
                <div className="nm">{e.label}</div>
                <div className="ds">
                  {e.url ? (
                    <a className="src-link" href={e.url} target="_blank" rel="noopener noreferrer">
                      {e.value} ↗
                    </a>
                  ) : (
                    e.value
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <SituationTree
        s={s}
        events={events}
        onSelectSignal={(id) => ops.selectSignal(id)}
        onSelectEvent={(id) => ops.selectEvent(id)}
      />

      {s.isRoot && <ChildrenSection signalId={s.id} onSelect={ops.selectSignal} />}

      <div className="dsec">
        {/* /event/* is the SPA route (full app view for members); /signal/* is the public
            article page served by the API for crawlers + sharing. */}
        <a className="src-link" href={`/event/${encodeURIComponent(s.id)}`} target="_blank" rel="noopener">
          Open as standalone route ↗
        </a>
      </div>
    </>
  );
}

function EventDetailBody({ ops, eventId }: { ops: OpsApi; eventId: string }) {
  const e = ops.getClient()?.events.find((x) => x.id === eventId);
  if (!e) return <div className="empty-hint">Event no longer active.</div>;
  const sev = e.severity ? SEVERITY_COLOR[e.severity] : "#f4b942";
  const md = e.metadata ?? {};
  const rows: Array<[string, string]> = [];
  if (md.magnitude != null) rows.push(["Magnitude", `${md.magnitude}${md.magtype ? ` ${md.magtype}` : ""}`]);
  if (md.depthKm != null) rows.push(["Depth", `${md.depthKm} km`]);
  if (md.place) rows.push(["Place", String(md.place)]);
  if (md.authority) rows.push(["Authority", String(md.authority)]);
  if (e.radius) rows.push(["Impact radius", `${e.radius} km`]);
  rows.push(["Position", `${e.lat.toFixed(2)}, ${e.lon.toFixed(2)}`]);
  if (e.timestamp) rows.push(["Occurred", `${timeAgo(e.timestamp)} · ${utcClock(e.timestamp)}`]);
  return (
    <>
      <div className="dsec">
        <div className="d-title">{e.title || e.type}</div>
        <div className="d-meta">
          <span
            className="sevtag"
            style={{ ["--sev" as string]: sev, color: sev, borderColor: sev }}
          >
            {e.severity ?? "event"}
          </span>
          <span className="d-id">
            {(e.type || "").toUpperCase()}
            {e.source ? ` · ${e.source}` : ""}
          </span>
        </div>
      </div>
      {e.summary && (
        <div className="dsec">
          <div className="h">Summary</div>
          <div className="sum">{e.summary}</div>
        </div>
      )}
      <div className="dsec">
        <div className="h">Parameters</div>
        <div className="assets">
          {rows.map(([k, v]) => (
            <div className="asset" key={k}>
              <div className="nm">{k}</div>
              <div className="ds">{v}</div>
            </div>
          ))}
        </div>
      </div>
      {e.sourceUrl && (
        <div className="dsec">
          <div className="h">Source</div>
          <a className="src-link" href={e.sourceUrl} target="_blank" rel="noopener noreferrer">
            {e.source || "link"} · open report ↗
          </a>
        </div>
      )}
      <div className="dsec">
        <div className="h">Reference</div>
        <div className="d-id">{e.id}</div>
      </div>
    </>
  );
}

interface ImpactRow {
  rule: string;
  severity?: string | null; // null = whole-category blend; set = per-severity bucket
  samples: number;
  hitRate: number;
  medianAbsZ: number;
  medianMovePct: number;
  importance: string; // learned severity | 'magnitude' (exempt rare-big) | 'learning'
}

const IMP_LABEL: Record<string, string> = {
  low: "LOW",
  medium: "MED",
  high: "HIGH",
  critical: "CRIT",
  magnitude: "MAG", // exempt: severity from the event's own magnitude (earthquakes/fires)
  learning: "…",
};
function impColor(imp: string): string | undefined {
  if (imp === "high" || imp === "critical") return "var(--green)";
  if (imp === "magnitude") return "var(--amber)";
  return undefined;
}

/** Per-category market-impact scorecard from /api/impact — the realized ABNORMAL move (z-score)
 *  of the right instrument in the hour after each signal, since tracking began. */
function ImpactScorecard() {
  const [rows, setRows] = useState<ImpactRow[] | null>(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const token = await getIdToken();
        const r = await fetch(`${config.restUrl}/api/impact`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!r.ok) throw new Error(String(r.status));
        const j = (await r.json()) as { scorecard?: ImpactRow[] };
        if (alive) setRows((j.scorecard ?? []).filter((x) => x.severity == null)); // overview = the blends
      } catch {
        if (alive) setErr(true);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);
  if (err) return <div className="empty-hint">Impact scorecard unavailable.</div>;
  if (!rows) return <div className="empty-hint">Loading impact…</div>;
  if (!rows.length) return <div className="empty-hint">No scored windows yet.</div>;
  return (
    <div className="impact-card">
      <div className="impact-row impact-head">
        <span className="r">Category</span>
        <span className="v">n</span>
        <span className="v">med σ</span>
        <span className="v">hit</span>
        <span className="v">imp</span>
      </div>
      {rows.map((r) => (
        <div className="impact-row" key={r.rule} title={`median 1h move ${r.medianMovePct}% · ${r.hitRate}% ≥2σ · importance ${r.importance}`}>
          <span className="r">{ruleTag(r.rule)}</span>
          <span className="v">{r.samples}</span>
          <span className="v" style={r.medianAbsZ >= 2 ? { color: "var(--green)" } : undefined}>
            {r.medianAbsZ.toFixed(2)}
          </span>
          <span className="v" style={r.hitRate >= 25 ? { color: "var(--green)" } : undefined}>
            {r.hitRate}%
          </span>
          <span className="v" style={{ color: impColor(r.importance) }}>
            {IMP_LABEL[r.importance] ?? r.importance}
          </span>
        </div>
      ))}
    </div>
  );
}

/** This rule's realized track record (from /api/impact) — the proof that closes the sale:
 *  "alerts like this have actually moved the market this often, this hard." Severity-split
 *  (founder rule): a HIGH alert shows past HIGHs of its category, never the low-dragged blend;
 *  the blend appears only while this tier's bucket is still empty. */
function SignalTrackRecord({ rule, severity }: { rule: string; severity?: string }) {
  const [row, setRow] = useState<ImpactRow | null | undefined>(undefined);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const token = await getIdToken();
        const r = await fetch(`${config.restUrl}/api/impact`, { headers: { Authorization: `Bearer ${token}` } });
        if (!r.ok) throw new Error(String(r.status));
        const j = (await r.json()) as { scorecard?: ImpactRow[] };
        const rows = j.scorecard ?? [];
        const bucket = severity ? rows.find((x) => x.rule === rule && x.severity === severity) : undefined;
        if (alive) setRow(bucket ?? rows.find((x) => x.rule === rule && x.severity == null) ?? null);
      } catch {
        if (alive) setRow(null);
      }
    })();
    return () => {
      alive = false;
    };
  }, [rule, severity]);
  if (!row) return null; // loading or no track record yet → show nothing rather than a sad row
  return (
    <div className="dsec">
      <div className="h">Track Record · this category{row.severity ? ` · ${row.severity.toUpperCase()} tier` : ""}</div>
      <div className="impact-card">
        <div className="impact-row" title={`median tradeable 4h move ${row.medianMovePct}% · ${row.hitRate}% ≥2σ · importance ${row.importance}`}>
          <span className="r">{row.samples} signals</span>
          <span className="v" style={row.medianAbsZ >= 2 ? { color: "var(--green)" } : undefined}>
            {row.medianAbsZ.toFixed(2)}σ
          </span>
          <span className="v" style={row.hitRate >= 25 ? { color: "var(--green)" } : undefined}>
            {row.hitRate}% ≥2σ
          </span>
          <span className="v" style={{ color: impColor(row.importance) }}>
            {IMP_LABEL[row.importance] ?? row.importance}
          </span>
        </div>
      </div>
      <div className="empty-hint" style={{ marginTop: 6 }}>
        Realized tradeable move — the furthest excursion within 4 traded hours of the alert —{" "}
        {row.severity
          ? `for ${row.severity.toUpperCase()} alerts of this category.`
          : "all severities blended (this tier's own history is still thin)."}
      </div>
    </div>
  );
}

function OverviewBody({ ops }: { ops: OpsApi }) {
  const signals = ops.getClient()?.signals ?? [];
  const counts = useMemo(() => {
    const c: Record<Severity, number> = { critical: 0, high: 0, medium: 0, low: 0 };
    signals.forEach((s) => c[s.severity]++);
    return c;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signals, ops.version]);
  const max = Math.max(1, ...Object.values(counts));
  const tk: Record<string, number> = {};
  signals.forEach((s) => s.tickers.forEach((t) => (tk[t] = (tk[t] ?? 0) + 1)));
  const topTk = Object.entries(tk)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 10);
  return (
    <>
      <div className="dsec">
        <div className="h">Active Signals · by severity</div>
        <div className="ov-sev">
          {(["critical", "high", "medium", "low"] as Severity[]).map((sv) => (
            <div className="row" key={sv}>
              <span className="lab">{sv}</span>
              <div className="bar">
                <span style={{ width: `${(counts[sv] / max) * 100}%`, background: SEVERITY_COLOR[sv] }} />
              </div>
              <span className="num">{counts[sv]}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="dsec">
        <div className="h">Market impact · since tracking</div>
        <ImpactScorecard />
        <div className="empty-hint" style={{ marginTop: 6 }}>
          Median <b>abnormal</b> move (in σ of the right instrument) over the next trading session
          after each signal; <b> hit</b> = share that moved ≥ 2σ. Internal measurement.
        </div>
      </div>
      <div className="dsec">
        <div className="h">Most-referenced tickers</div>
        <div className="ov-tickers">
          {topTk.length ? (
            topTk.map(([t, c]) => (
              <span className="tk" key={t}>
                {t} · {c}
              </span>
            ))
          ) : (
            <span className="empty-hint">No tickers on current signals.</span>
          )}
        </div>
      </div>
      <div className="dsec">
        <div className="h">Desk</div>
        <div className="empty-hint">
          Select a signal from the feed or a marker on the plot to open its drill-down — affected
          assets, recommended action, and trigger sources.
        </div>
      </div>
      <div className="dsec">
        <div className="h">Coverage</div>
        <div className="empty-hint">
          Alerts are <b style={{ color: "var(--green)" }}>global</b> — the feed is never gated by the
          map.{" "}
          {ops.isLive ? (
            <>
              Streaming live from <b style={{ color: "var(--green)" }}>api.earlywire.io</b>.
            </>
          ) : (
            <>
              Running on local <b style={{ color: "var(--amber)" }}>simulation</b>.
            </>
          )}
        </div>
      </div>
    </>
  );
}

// ── Price Chart ─────────────────────────────────────────────────────────────

interface ChartBar { t: number; o: number; h: number; l: number; c: number; v: number }

const RANGES = ['4h', '1d', '5d', '1mo'] as const;

function PriceChart({ signalId, ticker, eventMs }: { signalId: string; ticker: string; eventMs: number }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [bars, setBars] = useState<ChartBar[] | null>(null);
  const [currency, setCurrency] = useState<string | null>(null);
  const [range, setRange] = useState<string>('4h');
  const [err, setErr] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setBars(null);
    setErr(false);
    (async () => {
      try {
        const token = await getIdToken();
        const r = await fetch(
          `${config.restUrl}/api/signals/${encodeURIComponent(signalId)}/chart?range=${range}&ticker=${encodeURIComponent(ticker)}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!r.ok) throw new Error(String(r.status));
        const d = (await r.json()) as { bars: ChartBar[]; currency: string | null };
        if (!cancelled) { setBars(d.bars); setCurrency(d.currency); }
      } catch {
        if (!cancelled) setErr(true);
      }
    })();
    return () => { cancelled = true; };
  }, [signalId, ticker, range]);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !bars || bars.length === 0) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    ctx.scale(dpr, dpr);

    const pad = { top: 18, right: 48, bottom: 4, left: 4 };
    const cw = w - pad.left - pad.right;
    const ch = h - pad.top - pad.bottom;

    const minP = Math.min(...bars.map((b) => b.l));
    const maxP = Math.max(...bars.map((b) => b.h));
    const pRange = maxP - minP || 1;
    const minT = bars[0].t;
    const maxT = bars[bars.length - 1].t;
    const tRange = maxT - minT || 1;

    const x = (t: number) => pad.left + ((t - minT) / tRange) * cw;
    const y = (p: number) => pad.top + ch - ((p - minP) / pRange) * ch;

    ctx.strokeStyle = 'rgba(255,255,255,0.06)';
    ctx.lineWidth = 1;
    for (let i = 0; i < 4; i++) {
      const gy = pad.top + (ch * i) / 3;
      ctx.beginPath(); ctx.moveTo(pad.left, gy); ctx.lineTo(w - pad.right, gy); ctx.stroke();
    }

    ctx.fillStyle = 'rgba(255,255,255,0.35)';
    ctx.font = '9px monospace';
    ctx.textAlign = 'left';
    for (let i = 0; i < 4; i++) {
      const pv = maxP - (pRange * i) / 3;
      ctx.fillText(pv.toFixed(2), w - pad.right + 4, pad.top + (ch * i) / 3 + 3);
    }

    const barW = Math.max(1, Math.min(6, (cw / bars.length) * 0.6));
    for (const b of bars) {
      const bx = x(b.t);
      const bull = b.c >= b.o;
      const color = bull ? '#4ade80' : '#f87171';
      ctx.strokeStyle = color;
      ctx.lineWidth = 1;
      ctx.beginPath(); ctx.moveTo(bx, y(b.h)); ctx.lineTo(bx, y(b.l)); ctx.stroke();
      const bodyTop = y(Math.max(b.o, b.c));
      const bodyBot = y(Math.min(b.o, b.c));
      ctx.fillStyle = color;
      ctx.fillRect(bx - barW / 2, bodyTop, barW, Math.max(1, bodyBot - bodyTop));
    }

    if (eventMs >= minT && eventMs <= maxT) {
      const ex = x(eventMs);
      ctx.save();
      ctx.strokeStyle = 'rgba(255, 0, 0, 0.25)';
      ctx.lineWidth = 10;
      ctx.beginPath(); ctx.moveTo(ex, 0); ctx.lineTo(ex, h); ctx.stroke();
      ctx.strokeStyle = '#ff0000';
      ctx.lineWidth = 2.5;
      ctx.beginPath(); ctx.moveTo(ex, 0); ctx.lineTo(ex, h); ctx.stroke();
      ctx.fillStyle = '#ff0000';
      ctx.font = 'bold 9px monospace';
      ctx.textAlign = 'center';
      ctx.fillText('▼ EVENT', ex, pad.top - 6);
      ctx.restore();
    }

    const firstC = bars[0].c;
    const lastC = bars[bars.length - 1].c;
    const pctChg = ((lastC - firstC) / firstC) * 100;
    const sign = pctChg >= 0 ? '+' : '';
    ctx.fillStyle = pctChg >= 0 ? '#4ade80' : '#f87171';
    ctx.font = 'bold 10px monospace';
    ctx.textAlign = 'right';
    ctx.fillText(`${sign}${pctChg.toFixed(2)}%`, w - pad.right, pad.top - 6);
  }, [bars, eventMs]);

  useEffect(() => { draw(); }, [draw]);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <span className="mono-xs">{ticker}{currency ? ` · ${currency}` : ''}</span>
        <div style={{ display: 'flex', gap: 2 }}>
          {RANGES.map((r) => (
            <button
              key={r}
              onClick={() => setRange(r)}
              className="mono-xs"
              style={{
                background: range === r ? 'rgba(var(--green-rgb), 0.15)' : 'transparent',
                color: range === r ? 'var(--green)' : 'var(--ivory-d)',
                border: 'none', padding: '2px 6px', cursor: 'pointer',
              }}
            >{r}</button>
          ))}
        </div>
      </div>
      {err && <div className="mono-xs" style={{ color: 'var(--red)' }}>chart unavailable</div>}
      {!bars && !err && <div className="mono-xs">loading chart…</div>}
      {bars && bars.length === 0 && <div className="mono-xs">no bars (market closed?)</div>}
      {bars && bars.length > 0 && (
        <canvas ref={canvasRef} style={{ width: '100%', height: 140, background: 'var(--bg-2)', borderRadius: 2 }} />
      )}
    </div>
  );
}

// ── Children Section ────────────────────────────────────────────────────────

function ChildrenSection({ signalId, onSelect }: { signalId: string; onSelect: (id: string) => void }) {
  const [children, setChildren] = useState<Signal[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await getIdToken();
        const r = await fetch(`${config.restUrl}/api/signals/${encodeURIComponent(signalId)}/children`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!r.ok) throw new Error(String(r.status));
        const d = (await r.json()) as { children: Signal[] };
        if (!cancelled) setChildren(d.children);
      } catch {
        if (!cancelled) setChildren([]);
      }
    })();
    return () => { cancelled = true; };
  }, [signalId]);

  if (children === null || children.length === 0) return null;

  return (
    <div className="dsec">
      <div className="h">Impact Chain · {children.length}</div>
      <div className="assets">
        {children.map((c) => (
          <div className="asset" key={c.id} style={{ cursor: 'pointer' }} onClick={() => onSelect(c.id)}>
            <div>
              <div className="nm" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', background: SEVERITY_COLOR[c.severity] }} />
                <span style={{ color: SEVERITY_COLOR[c.severity], fontSize: 9, fontWeight: 700 }}>{c.severity.toUpperCase()}</span>
                {c.status === 'live' && <span style={{ color: 'var(--green)', fontSize: 8 }}>LIVE</span>}
              </div>
              <div style={{ fontSize: 12, color: 'var(--ivory)', marginTop: 4 }}>{c.title}</div>
              {c.tickers?.length > 0 && (
                <div style={{ marginTop: 4, display: 'flex', gap: 4 }}>
                  {c.tickers.map((t) => <span key={t} className="tk">{t}</span>)}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
